import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import orderBy from "lodash/orderBy";
import Stripe from "stripe";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import useWebSignupStore from "./store";
import { webSignupCheckoutSessionCache } from "./checkoutSession";
import { WebSignupCheckoutSession } from "../../utils/webSignupCheckoutSession";
import { bugNotif } from "../../api/bugsnag";
import {
  identifyWebSignupLogRocketUser,
  identifyWebSignupProspect,
  stashWebSignupLogRocketSave,
} from "../../utils/webLogRocketRecord";
import { getWLPromoEntries, isPromoValid } from "../../services/promo";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useFormsStore } from "../../../Common/store/common";
import {
  createSubscriptionSinglePage,
  validateStripePromo,
} from "../../api/stripe";
import { getErrorMsg, useEffectOnlyOnce } from "../../../Common/utils/common";
import { isExistingActiveMemberWebSignupError } from "../../../Common/utils/webSignupExistingMemberError";
import { ViewWLSubscriptionsTiers } from "../../../Common/models/subscriptions";
import {
  identifyKlaviyoCompleteProfile,
  trackSignupAddToCart,
} from "../../services/marketing";
import { getSubscriptionListStripe } from "../../services/subscription";
import {
  isClubPlan,
  resolveJoinPlanFromList,
} from "../../../Common/constants/clubMembership";
import { getWlConfig } from "../../services/config";
import { getRefferedBonusEntries } from "../../utils/promo";
import { SIGN_IN_PROVIDER_MAP } from "../../../Common/models/user";
import {
  logWebSignupTiming,
  logWebSignupTimingOnce,
  markWebSignupPageStart,
  safeSignupTimingStep,
  timeWebSignupAsync,
} from "./signupTiming";
import {
  applyWebSignupBootstrapIfPresent,
  hasUsableWebSignupBootstrapInLocation,
  isWebSignupCatalogFetchEnabled,
} from "./applyWebSignupBootstrap";
import {
  readSignupPreferencePropertiesFromLocation,
  readSignupStateFromLocation,
} from "../../../Common/utils/webSignupLinkParams";

export const useWebSignupBootstrapProvider = () => {
  markWebSignupPageStart();
  // Apply synchronously once so subscription/promo providers see bootstrap on first render
  // and do not kick off blocking prerequisite fetches.
  const didApplyBootstrapRef = useRef(false);
  if (!didApplyBootstrapRef.current) {
    didApplyBootstrapRef.current = true;
    applyWebSignupBootstrapIfPresent();
    const resolvedState = readSignupStateFromLocation();
    if (resolvedState) {
      const { userState, setUserState } = useWebSignupStore.getState();
      if (!userState) {
        setUserState(resolvedState);
      }
    }
  }
};

export const useWebSignupProvider = () => {
  markWebSignupPageStart();
  const { setFormLoading } = useFormsStore();

  const {
    setFirstName,
    setLastName,
    setEmailAddress,
    setUserState,
    setPhoneNumber,
    validPromoCode,
    validStripeCoupon,
    selectedSubscription,
  } = useWebSignupStore();

  const [error, setError] = useState("");
  const [redirectToApp, setRedirectToApp] = useState(false);
  const isSubmitInFlightRef = useRef(false);

  const useFormMethods = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { handleSubmit } = useFormMethods;

  const submit = async (data: any) => {
    if (isSubmitInFlightRef.current) {
      return;
    }
    isSubmitInFlightRef.current = true;

    setFormLoading(true);
    setError("");
    const { firstName, lastName, phoneNumber, emailAddress, suburb } = data as {
      firstName: string;
      lastName: string;
      phoneNumber: string;
      emailAddress: string;
      suburb: string;
    };

    setFirstName(firstName);
    setLastName(lastName);
    setPhoneNumber(phoneNumber);
    setEmailAddress(emailAddress);
    setUserState(suburb);

    identifyWebSignupProspect({
      email: emailAddress,
      firstName,
      lastName,
      phoneNumber,
    });

    try {
      const paymentValidationError =
        await useWebSignupStore.getState().validatePaymentElement();
      if (paymentValidationError) {
        setFormLoading(false);
        setError(paymentValidationError);
        return;
      }

      let source = "unknown";
      let campaign = "unknown";
      let content = "unknown";
      let medium = "unknown";
      let wlLanding = "unknown";
      const fbSource = new URLSearchParams(window.location.search).get(
        "fbclid",
      );

      if (!isNull(fbSource) && !isEmpty(fbSource)) {
        source = "Facebook";
      }

      const tiktokSource = new URLSearchParams(window.location.search).get(
        "ttclid",
      );

      if (!isNull(tiktokSource) && !isEmpty(tiktokSource)) {
        source = "Tiktok";
      }

      const urlSource = new URLSearchParams(window.location.search).get(
        "utm_source",
      );

      if (!isNull(urlSource) && !isEmpty(urlSource)) {
        source = urlSource;
      }

      const campaignSource = new URLSearchParams(window.location.search).get(
        "utm_campaign",
      );

      if (!isNull(campaignSource) && !isEmpty(campaignSource)) {
        campaign = campaignSource;
      }

      const contentSource = new URLSearchParams(window.location.search).get(
        "utm_content",
      );

      if (!isNull(contentSource) && !isEmpty(contentSource)) {
        content = contentSource;
      }

      const mediumSource = new URLSearchParams(window.location.search).get(
        "utm_medium",
      );

      if (!isNull(mediumSource) && !isEmpty(mediumSource)) {
        medium = mediumSource;
      }

      const wlLandingSource = new URLSearchParams(window.location.search).get(
        "wlLanding",
      );

      if (!isNull(wlLandingSource) && !isEmpty(wlLandingSource)) {
        wlLanding = wlLandingSource;
      }

      const referredByMemberId = new URLSearchParams(
        window.location.search,
      ).get("referredByMemberId");

      const checkoutSessionKey = webSignupCheckoutSessionCache.buildKey({
        emailAddress,
        phoneNumber,
        priceId: selectedSubscription?.stripePriceId || "",
        couponId: validStripeCoupon?.id || "",
        promoCode: validPromoCode || "",
        alternativePriceVersion:
          selectedSubscription?.alternativePriceVersion ?? "",
        referralBonusLookUpKey:
          useWebSignupStore.getState().referralConfig?.lookUpKey ?? null,
      });

      let checkoutSession: WebSignupCheckoutSession | null =
        webSignupCheckoutSessionCache.get(checkoutSessionKey);

      if (checkoutSession === null) {
        const result = await createSubscriptionSinglePage({
          ...data,
          emailAddress,
          source,
          campaign,
          wlLanding,
          content,
          medium,
          referredByMemberId,
          referralBonusLookUpKey:
            useWebSignupStore.getState().referralConfig?.lookUpKey ?? null,
          signInProvider: SIGN_IN_PROVIDER_MAP.inAppPhoneNumber,
          priceId: selectedSubscription?.stripePriceId,
          couponId: validStripeCoupon?.id,
          promoCode: validPromoCode || "",
          signupPreferenceProperties:
            readSignupPreferencePropertiesFromLocation(),
          ...(selectedSubscription?.alternativePriceVersion !== undefined
            ? {
                alternativePriceVersion:
                  selectedSubscription.alternativePriceVersion,
              }
            : {}),
        });

        checkoutSession = {
          userId: result.data.userId,
          clientSecret: result.data.clientSecret,
          paymentReferenceId: result.data.subscriptionId,
        };

        webSignupCheckoutSessionCache.save({
          checkoutSessionKey,
          session: checkoutSession,
        });

        identifyWebSignupLogRocketUser({
          userId: checkoutSession.userId,
          email: emailAddress,
          firstName,
          lastName,
          phoneNumber,
        });
        stashWebSignupLogRocketSave({
          userId: checkoutSession.userId,
          token: result.data.logRocketSaveToken,
        });
      }

      await useWebSignupStore.getState().submitPayment();

      await identifyKlaviyoCompleteProfile(firstName, lastName, phoneNumber);

      // setFormLoading(false);
    } catch (error) {
      setFormLoading(false);
      webSignupCheckoutSessionCache.clear();
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      if (isExistingActiveMemberWebSignupError(error)) {
        setRedirectToApp(true);
      } else {
        setError(errorMsg);
        bugNotif("StepCompleteProfile", rawErrorMsg);
      }
    } finally {
      isSubmitInFlightRef.current = false;
    }
  };

  return {
    useFormMethods,
    error,
    redirectToApp,
    setRedirectToApp,
    onSubmit: handleSubmit(submit),
  };
};

export const useWebSignupPromoCodeProvider = () => {
  markWebSignupPageStart();
  const { setFormLoading } = useFormsStore();

  const {
    validPromoCodePromo,
    selectedSubscription,
    setValidPromoCode,
    setValidStripeCoupon,
    setValidPromoCodePromo,

    setPromoEntries,
  } = useWebSignupStore();

  // to validate internal promo
  const validateWLPromoCode = async (promoCode: string) => {
    setValidPromoCodePromo(undefined);
    setValidPromoCode(undefined);
    try {
      const result = await timeWebSignupAsync(
        "prerequisite: /promo/validateWLPromo",
        () =>
          isPromoValid({
            code: promoCode,
            forSignUp: true,
          }),
      );
      const testedPromoCode = !isNull(result) ? result.code : "";
      setValidPromoCodePromo(result);
      setValidPromoCode(testedPromoCode);
      console.log("got valid promo!", testedPromoCode);
      return !isEmpty(testedPromoCode);
    } catch (error) {
      setValidPromoCodePromo(null);
      setValidPromoCode("");
      bugNotif("debouncedValidatePromo", getErrorMsg(error).rawErrorMsg);
      return false;
    }
  };

  // to validate stripe coupon
  const validateStripePromoCode = async (promoCode: string) => {
    setValidStripeCoupon(undefined);
    try {
      const result = await timeWebSignupAsync(
        "prerequisite: /stripe/validatePromo",
        () =>
          validateStripePromo({
            promoCode,
          }),
      );
      const coupon = result.data.coupon as Stripe.Coupon;
      console.log(
        "now checking stripe coupon against ",
        coupon,
        selectedSubscription,
      );
      if (
        coupon.applies_to !== undefined &&
        selectedSubscription !== undefined &&
        !isNull(selectedSubscription) &&
        coupon.applies_to.products.indexOf(selectedSubscription.docId) === -1
      ) {
        setValidStripeCoupon(null);
        logWebSignupTimingOnce(
          "valid-stripe-coupon-settled",
          "prerequisite: validStripeCoupon settled",
          { hasCoupon: false, reason: "coupon does not apply to selected plan" },
        );
        return false;
      }
      // console.log("got valid stripe promo!", coupon);

      setValidStripeCoupon(coupon);
      logWebSignupTimingOnce(
        "valid-stripe-coupon-settled",
        "prerequisite: validStripeCoupon settled",
        { hasCoupon: true, couponId: coupon.id },
      );

      return true;
    } catch (error) {
      setValidStripeCoupon(null);
      logWebSignupTimingOnce(
        "valid-stripe-coupon-settled",
        "prerequisite: validStripeCoupon settled",
        { hasCoupon: false, reason: "validatePromo failed or invalid" },
      );
      bugNotif("debouncedValidatePromo", getErrorMsg(error).rawErrorMsg);
      return false;
    }
  };

  // to validate promo and stripe coupon simultaneously
  const validatePromoCode = async (_promoCode = "") => {
    setFormLoading(true);
    const code = (_promoCode || "").trim().toUpperCase();
    // console.log("checking --- ", { code, overrideCode });
    const validationResults = await Promise.all([
      validateWLPromoCode(code),
      validateStripePromoCode(code),
    ]);

    setFormLoading(false);

    const result =
      compact(validationResults).length > 0 ||
      isEmpty(code) ||
      "Invalid promo code";
    if (typeof result === "string") {
      // setError(result);
    } else {
      // clearErrors(); // recheck!
    }
    console.log("got result result", result, validationResults, code);
    // refresh stripe form here get another clientsecretid after update

    return result;
  };

  // Initial handling if promo code is not undefined on load.
  // Skip when bootstrap query params already settled coupon/promo for mount.
  useEffectOnlyOnce(() => {
    if (useWebSignupStore.getState().bootstrapFromQuery) {
      return;
    }

    const promoCode = new URLSearchParams(window.location.search).get("promo");
    if (!isNull(promoCode) && !isEmpty(promoCode)) {
      const promo = promoCode.toUpperCase().trim();
      // setValue(promoField.name, promo);
      // trigger(promoField.name);
      console.log("will check promo from query");
      logWebSignupTiming("prerequisite: promo query detected, validating", {
        promo,
      });
      validatePromoCode(promo);
    } else {
      setValidPromoCode("");
      setValidStripeCoupon(null);
      setValidPromoCodePromo(null);
      logWebSignupTimingOnce(
        "valid-stripe-coupon-settled",
        "prerequisite: validStripeCoupon settled",
        { hasCoupon: false, reason: "no promo query param" },
      );
    }
  });

  useEffect(() => {
    if (
      !isNull(validPromoCodePromo) &&
      validPromoCodePromo !== undefined &&
      !isNull(selectedSubscription) &&
      selectedSubscription !== undefined
    ) {
      const promoEntries = getWLPromoEntries(
        validPromoCodePromo,
        selectedSubscription,
      );
      setPromoEntries(promoEntries);
    } else {
      setPromoEntries(undefined);
    }
  }, [validPromoCodePromo, selectedSubscription]);
};

export const useSubscriptionsProvider = () => {
  markWebSignupPageStart();
  const pageParams = useParams();
  const {
    setSubscriptionList,
    selectedSubscription,
    setSelectedSubscription,
    bootstrapFromQuery,
  } = useWebSignupStore();

  // GET OPTIONS FOR SUBSCRIPTION — skipped when bootstrap query hydrated mount prereqs.
  const { data: subscriptionList, error: subscriptionListError } = useQuery({
    queryKey: ["GET_SUBSCRIPTION_WEB_SIGNUP", pageParams],
    queryFn: async () => {
      // const priceDiscoveryVersion = new URLSearchParams(
      //   window.location.search
      // ).get("pdv");
      const subscriptionName = (
        (pageParams as { subscriptionKey?: string }).subscriptionKey || "pro"
      )
        .toLowerCase()
        .trim();
      const isClubJoin = subscriptionName === "club";

      const resp = await timeWebSignupAsync(
        "prerequisite: getSubscriptionListStripe total",
        () =>
          getSubscriptionListStripe(
            "version2",
            false,
            {
              onStep: safeSignupTimingStep,
            },
            { includeClub: isClubJoin },
          ),
      );

      const plans = isClubJoin ? resp.filter(isClubPlan) : resp;
      if (isClubJoin && plans.length === 0) {
        throw new Error("Club membership is not available.");
      }

      return orderBy(plans, (sub) => sub.price, "asc");
    },
    enabled: isWebSignupCatalogFetchEnabled({
      bootstrapFromQuery,
      hasUsableLocationBootstrap: hasUsableWebSignupBootstrapInLocation(),
    }),
    refetchOnWindowFocus: false,
  });

  // To handle subscription list get error
  useEffect(() => {
    if (!isNull(subscriptionListError)) {
      bugNotif(
        "StepEmailSetup - getSubscriptions",
        getErrorMsg(subscriptionListError).rawErrorMsg,
      );
    }
  }, [subscriptionListError]);

  // to handle update subscription selection. Mostly used by upsell component.
  // Can be used also when changing plans
  const updateSelectedSubscription = (
    subscription: ViewWLSubscriptionsTiers,
  ) => {
    setSelectedSubscription(subscription);

    const pid = subscription.docId;
    const value = subscription.price;
    trackSignupAddToCart({
      subscriptionId: pid,
      itemName: subscription.name,
      value,
      type: "Membership",
    });
  };

  // To handle selected subscription.
  useEffect(() => {
    if (bootstrapFromQuery) {
      return;
    }

    if (subscriptionList !== undefined) {
      if (selectedSubscription === undefined || isNull(selectedSubscription)) {
        const { subscriptionKey } = pageParams as {
          subscriptionKey: string | undefined;
        };

        const subscriptionName = (subscriptionKey || "pro")
          .toLowerCase()
          .trim();
        const matchedSubscription = resolveJoinPlanFromList(
          subscriptionList,
          subscriptionName,
        );
        if (!matchedSubscription) {
          return;
        }
        updateSelectedSubscription(matchedSubscription);
        logWebSignupTimingOnce(
          "selected-subscription-ready",
          "prerequisite: selectedSubscription ready",
          {
            subscriptionName: matchedSubscription.name,
            price: matchedSubscription.price,
            bootstrapSource: "api",
          },
        );

        setSubscriptionList(subscriptionList);
      }
    }
  }, [subscriptionList, bootstrapFromQuery]);
};

export const useGetReferralEntries = () => {
  const [referralParam, setReferralParam] = useState<
    | { referredByMemberId: string; referralBonusLookUpKey: string | null }
    | undefined
  >(undefined);
  const {
    selectedSubscription,
    referralConfig,
    setReferralConfig,
    setReferredBonusEntries,
  } = useWebSignupStore();

  const { data, error } = useQuery({
    queryKey: ["WLCONFIG", referralParam],
    queryFn: async () => {
      if (referralParam) {
        const resp = await getWlConfig();
        return resp;
      }
    },
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "useGetReferralEntries - getWLConfigProvider",
        getErrorMsg(error).rawErrorMsg,
      );
    }
  }, [error]);

  useEffect(() => {
    const configs = data?.referralConfig;
    const lookupKey = referralParam?.referralBonusLookUpKey;

    if (!Array.isArray(configs) || configs.length === 0) return;

    const referralConfig =
      configs.find(({ lookUpKey }) => lookUpKey === lookupKey) ??
      configs.find(({ isDefault }) => isDefault) ??
      configs[0];

    setReferralConfig(referralConfig);
  }, [data, referralParam]);

  useEffect(() => {
    if (
      referralConfig !== undefined &&
      !isNull(referralConfig) &&
      selectedSubscription !== undefined &&
      !isNull(selectedSubscription)
    ) {
      const referedBonusEntries = getRefferedBonusEntries(
        referralConfig.referredBonus,
        selectedSubscription.entriesPerMonth,
      );
      const twoWayBonusEntries = getRefferedBonusEntries(
        referralConfig.twoWayBonus,
        selectedSubscription.entriesPerMonth,
      );

      setReferredBonusEntries({
        referedBonusEntries,
        twoWayBonusEntries,
        referedTotalEntries:
          referedBonusEntries.value + twoWayBonusEntries.value,
      });
    } else {
      setReferredBonusEntries(undefined);
    }
  }, [referralConfig, selectedSubscription]);

  useEffect(() => {
    const referredByMemberId = new URLSearchParams(window.location.search).get(
      "referredByMemberId",
    );

    const referralBonusLookUpKey = new URLSearchParams(
      window.location.search,
    ).get("referralBonusLookUpKey");

    if (!isNull(referredByMemberId)) {
      setReferralParam({
        referredByMemberId,
        referralBonusLookUpKey,
      });
    }
  }, []);
};
